import React, { forwardRef, useImperativeHandle, useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from "../bootstrap/index.jsx";
import { Adalicon, FaIcon } from "../common/Icons.jsx";
import DropdownMenu from '../common/DropdownMenu.jsx';
import { fetchJson } from '../../helpers/fetch.js';
import { WithPermission } from './Toolbar.jsx';
import PresetsManagementModal from './PresetsManagementModal.jsx';

import { datagrid_create_preset_path } from '~/routes';

const MENU_ITEM_STYLE = {
  display: 'grid', 
  gridTemplateColumns: '1.5rem 1fr', 
  padding: '0.5rem 3rem 0.5rem 1.5rem',
  gap: '1rem',
};


const PresetMenuItems = ({presets, saveState, resetState, restoreState, permissions = {}, setShowManagementModal, setOpen}) => {
  const [newPresetName, setNewPresetName] = useState(undefined);

  const handleSave = () => {
    saveState(newPresetName);
    setNewPresetName(undefined);
  }

  return <>
    <li style={MENU_ITEM_STYLE} onClick={resetState}>
      <FaIcon type='refresh'/><span>{I18n.t('reset')}</span>
    </li>
    <li role="separator" className="divider"></li>
    <WithPermission permissions={permissions} permission='presets_manage'>
      <li style={MENU_ITEM_STYLE} onClick={() => setNewPresetName('')}><FaIcon type='bookmark'/><span>{I18n.t('preset_save')}</span></li>
      <li role="separator" className="divider"></li>
    </WithPermission>
    {presets.length == 0 && newPresetName == undefined && <li style={MENU_ITEM_STYLE} className='text-disabled'><span/><span>{I18n.t('no_presets')}</span></li>}
    {newPresetName != undefined && <li style={MENU_ITEM_STYLE}>
        <span/>
        <span>
          <input
            className='form-control input-sm'
            style={{
              display: 'inline-block',
              width: 'calc(100% - 35px)'
            }} 
            type='text' 
            value={newPresetName} 
            autoFocus 
            onChange={e => setNewPresetName(e.target.value)} 
            placeholder={I18n.t('name')}
          />
          <Button size='xs' onClick={handleSave} style={{marginLeft: '0.5rem', lineHeight: 1.25}}><FaIcon type='check' /></Button>
        </span>
    </li>}
    {presets.map(preset =>
      <li key={preset.name} className='text-truncate' title={preset.name} style={MENU_ITEM_STYLE} onClick={() => {restoreState(preset); setOpen(false)}}>
        <span/>
        <span className='text-truncate'>{preset.name}</span>
      </li>
    )}
    <WithPermission permissions={permissions} permission='presets_manage'>
      <li role="separator" className="divider"></li>
      <li style={MENU_ITEM_STYLE} onClick={() => setShowManagementModal(true)}>
        <FaIcon type='cog'/><span>{I18n.t('manage_presets')}...</span>
      </li>
    </WithPermission>
  </>
}

export const restoreDefaultPreset = (datagrid, gridRef) => {
  const defaultPreset = (datagrid?.presets || []).find(preset => preset.default)
  if (defaultPreset) {
    restorePreset(defaultPreset, gridRef)
  }
}

export const restorePreset = (preset, gridRef) => {
  gridRef.current.api.applyColumnState({
    state: preset.column_state,
    applyOrder: true,
  });
  gridRef.current.api.setFilterModel(preset.filter_model);
  gridRef.current.api.setGridOption('pivotMode', preset.column_state?.some(a => a.pivot));
}

export const clearPreset = (gridRef) => {
  const api = gridRef.current?.api;
  if (!api) return;

  api.setFilterModel(null);
  api.setGridOption('sortModel', null);
  api.resetColumnState();
  api.setRowGroupColumns([]);
  api.setPivotColumns([]);
  gridRef.current.api.setGridOption('pivotMode', false);
};

const Presets = ({gridRef, datagrid, context, permissions = {}}) => {
  const [presets, setPresets] = useState(datagrid?.presets || []);
  const [showManagementModal, setShowManagementModal] = useState(false);

  const saveState = useCallback((name) => {
    const newPreset = {
      name: name, 
      column_state: gridRef.current.api.getColumnState(),
      filter_model: gridRef.current.api.getFilterModel()
    }
    fetchJson(
      datagrid_create_preset_path({datagrid_id: datagrid.id, context_name: context.context_name, _options: true}),
      {body: JSON.stringify({...newPreset}), method: 'POST'}
    )
      .then(res => {
        setPresets([newPreset, ...presets]);
      })
  });

  const restoreState = useCallback((preset) => {
    restorePreset(preset, gridRef);
  });

  const resetState = useCallback(() => {
    gridRef.current.api.resetColumnState();
    gridRef.current.api.setFilterModel([]);
    gridRef.current.api.setGridOption('pivotMode', false);
  });

  return <WithPermission permissions={permissions} permission='presets_view'>
    <PresetsManagementModal 
      presets={presets} 
      show={showManagementModal} 
      setShow={setShowManagementModal} 
      datagrid_id={datagrid.id}
      context_name={context.context_name}
      setPresets={setPresets}
      displayPreset={restoreState}
    />
    <DropdownMenu
      menuProps={{style: {width: 300}}}
      menuItems={
        <PresetMenuItems
          saveState={saveState}
          resetState={resetState}
          restoreState={restoreState}
          presets={presets}
          permissions={permissions}
          setShowManagementModal={setShowManagementModal}
        />
      }
    >
      <Button type='default'>
        <FaIcon className='mr-1' type='bookmark'/>
        {I18n.t('presets')}
        <span className='caret'/>
        </Button>
    </DropdownMenu>
  </WithPermission>
}

export default Presets;